import { useMemo, useState } from 'react'
import {
  App, Badge, Button, Card, Col, Empty, Form, Input, Modal, Row, Select,
  Space, Switch, Tag, Typography,
} from 'antd'
import { PlayCircleOutlined, CheckCircleOutlined, ToolOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  failureReasonsApi, sparesApi, workOrdersApi,
} from '@/features/maintenance/api'
import { machinesApi } from '@/features/masters/machines/api'
import PageHeader from '@/shared/components/PageHeader'
import { useAuthStore } from '@/features/auth/store'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { MaintenanceWorkOrder, SparePart } from '@/shared/types/maintenance'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'

const { Text, Title } = Typography

/**
 * Spec §6 + §7 — Technician's My Jobs board + Execution Form.
 *
 * Tablet-first card layout (operator pattern). Only shows WOs assigned to
 * the logged-in technician. Buttons reflect lifecycle state. Cost fields
 * are deliberately absent (spec §15) — labour cost is computed by backend
 * from TechnicianProfile.hourly_cost × repair_minutes/60.
 */
export default function TechnicianMyJobsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const me = useAuthStore((s) => s.user)
  const [executingWO, setExecutingWO] = useState<MaintenanceWorkOrder | null>(null)
  const [spareModalOpen, setSpareModalOpen] = useState(false)

  const { data: jobs, isLoading } = useQuery({
    queryKey: ['my-work-orders', plantId, me?.id],
    enabled: !!plantId && !!me?.id,
    queryFn: () => workOrdersApi.list({
      plant_id: plantId, assigned_to: me!.id, only_open: false, limit: 200,
    }),
  })

  const { data: machinesData } = useQuery({
    queryKey: ['machines-for-maint', plantId],
    enabled: !!plantId,
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 500 }),
  })
  const machines = machinesData?.items ?? []
  const machineLabel = (id: string | null) =>
    id ? machines.find((m) => m.id === id)?.name ?? machines.find((m) => m.id === id)?.machine_id_str ?? '—' : '—'

  const { data: failureReasons } = useQuery({
    queryKey: ['failure-reasons'],
    queryFn: () => failureReasonsApi.list(),
  })

  const { data: spares } = useQuery({
    queryKey: ['spares-list'],
    queryFn: () => sparesApi.list({ limit: 1000 }),
  })

  // ── Group jobs ────────────────────────────────────────────────────────
  const groups = useMemo(() => {
    const j = jobs ?? []
    const today = dayjs().format('YYYY-MM-DD')
    return {
      assigned:    j.filter((x) => x.status === 'ASSIGNED' || x.status === 'REPORTED'),
      inProgress:  j.filter((x) => x.status === 'IN_PROGRESS'),
      waiting:     j.filter((x) => x.status === 'WAITING_FOR_SPARES'),
      completedToday: j.filter((x) =>
        x.status === 'COMPLETED' && x.actual_end?.startsWith(today),
      ),
      overdue:     j.filter((x) => x.status !== 'CLOSED' && x.planned_end && dayjs(x.planned_end).isBefore(dayjs())),
    }
  }, [jobs])

  // ── Lifecycle mutations ───────────────────────────────────────────────
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['my-work-orders'] })
    qc.invalidateQueries({ queryKey: ['work-orders'] })
  }

  const startMutation = useMutation({
    mutationFn: (id: string) => workOrdersApi.start(id),
    onSuccess: (wo) => { message.success('Job started'); setExecutingWO(wo); refresh() },
    onError: () => message.error('Could not start'),
  })
  const waitMutation = useMutation({
    mutationFn: (id: string) => workOrdersApi.waitForSpares(id),
    onSuccess: () => { message.success('Marked WAITING_FOR_SPARES'); refresh() },
    onError: () => message.error('Could not change status'),
  })

  // Execution form
  const [execForm] = Form.useForm()
  const completeMutation = useMutation({
    mutationFn: (vals: {
      root_cause_id: string
      action_taken: string
      closure_remarks?: string
      trial_run_done?: boolean
      production_informed?: boolean
    }) => {
      const remarksParts: string[] = []
      if (vals.trial_run_done) remarksParts.push('Trial run done')
      if (vals.production_informed) remarksParts.push('Production informed')
      return workOrdersApi.complete(executingWO!.id, {
        root_cause_id: vals.root_cause_id,
        action_taken: vals.action_taken,
        closure_remarks: [vals.closure_remarks, ...remarksParts].filter(Boolean).join(' • '),
      })
    },
    onSuccess: () => {
      message.success('Job completed — pending supervisor approval')
      setExecutingWO(null)
      refresh()
    },
    onError: (e) => message.error((e as { message?: string })?.message ?? 'Complete failed'),
  })

  // Spare request modal
  const [spareForm] = Form.useForm()
  const issueSpareMutation = useMutation({
    mutationFn: (vals: { spare_id: string; issued_qty: number; remarks?: string }) =>
      sparesApi.issue({
        spare_id: vals.spare_id,
        work_order_id: executingWO!.id,
        issued_qty: vals.issued_qty,
        remarks: vals.remarks,
      }),
    onSuccess: () => {
      message.success('Spare requested — store will issue against this WO')
      setSpareModalOpen(false)
      qc.invalidateQueries({ queryKey: ['spares-list'] })
    },
    onError: (e) => message.error((e as { message?: string })?.message ?? 'Spare request failed'),
  })

  const renderJob = (wo: MaintenanceWorkOrder) => {
    const ageing = Math.max(0, Math.round(dayjs().diff(dayjs(wo.reported_datetime), 'hour', true)))
    return (
      <Card
        key={wo.id}
        size="small"
        style={{ marginBottom: 12 }}
        title={(
          <Space size={8} wrap>
            <Text strong>{wo.work_order_no}</Text>
            <MaintenanceStatusBadge status={wo.priority} />
            <MaintenanceStatusBadge status={wo.status} />
            {wo.maintenance_type === 'EMERGENCY' && <Tag color="red">SAFETY</Tag>}
          </Space>
        )}
        extra={<Text type="secondary">{ageing}h old</Text>}
      >
        <Row gutter={8}>
          <Col xs={24} md={14}>
            <Text type="secondary">Machine</Text><br />
            <Text strong>{machineLabel(wo.machine_id)}</Text>
          </Col>
          <Col xs={24} md={10}>
            <Text type="secondary">Reported</Text><br />
            {dayjs(wo.reported_datetime).format('DD MMM HH:mm')}
          </Col>
        </Row>
        <Space wrap style={{ marginTop: 12 }}>
          {(wo.status === 'ASSIGNED' || wo.status === 'WAITING_FOR_SPARES' || wo.status === 'REOPENED') && (
            <Button type="primary" icon={<PlayCircleOutlined />}
              onClick={() => startMutation.mutate(wo.id)} loading={startMutation.isPending}>
              Start job
            </Button>
          )}
          {wo.status === 'IN_PROGRESS' && (
            <>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => {
                execForm.resetFields()
                setExecutingWO(wo)
              }}>
                Complete job
              </Button>
              <Button icon={<ToolOutlined />} onClick={() => {
                spareForm.resetFields()
                setExecutingWO(wo)
                setSpareModalOpen(true)
              }}>
                Request spare
              </Button>
              <Button onClick={() => waitMutation.mutate(wo.id)} loading={waitMutation.isPending}>
                Mark waiting for spare
              </Button>
            </>
          )}
        </Space>
      </Card>
    )
  }

  const sectionHeader = (label: string, count: number, color: string) => (
    <Title level={5} style={{ marginTop: 16 }}>
      <Badge color={color} text={label} /> <Text type="secondary">({count})</Text>
    </Title>
  )

  return (
    <div>
      <PageHeader
        title="My Jobs"
        subtitle={`Logged in as ${me?.full_name ?? me?.username ?? ''}`}
      />

      {isLoading && <Empty description="Loading…" />}

      {sectionHeader('New / Assigned', groups.assigned.length, 'blue')}
      {groups.assigned.length === 0
        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing newly assigned" />
        : groups.assigned.map(renderJob)}

      {sectionHeader('In Progress', groups.inProgress.length, 'orange')}
      {groups.inProgress.length === 0
        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No active job" />
        : groups.inProgress.map(renderJob)}

      {sectionHeader('Waiting for Spares', groups.waiting.length, 'gold')}
      {groups.waiting.map(renderJob)}

      {sectionHeader('Completed today', groups.completedToday.length, 'green')}
      {groups.completedToday.map(renderJob)}

      {/* Execution form modal — spec §7 */}
      <Modal
        title={`Complete job ${executingWO?.work_order_no ?? ''}`}
        open={!!executingWO && !spareModalOpen}
        onOk={() => execForm.validateFields().then((v) => completeMutation.mutate(v))}
        onCancel={() => setExecutingWO(null)}
        confirmLoading={completeMutation.isPending}
        width={560}
      >
        <Form form={execForm} layout="vertical">
          <Form.Item
            name="root_cause_id" label="Root cause"
            rules={[{ required: true, message: 'Root cause is required' }]}>
            <Select
              showSearch
              placeholder="Select reason from master"
              options={(failureReasons ?? []).map((r) => ({
                label: `${r.category} — ${r.reason_name}`,
                value: r.id,
              }))}
              filterOption={(i, o) => (o?.label as string).toLowerCase().includes(i.toLowerCase())}
            />
          </Form.Item>
          <Form.Item
            name="action_taken" label="Action taken"
            rules={[{ required: true, message: 'Action taken is required' }, { min: 5 }]}>
            <Input.TextArea rows={3} placeholder="Replaced bearing, aligned shaft, ran trial..." />
          </Form.Item>
          <Form.Item
            name="trial_run_done" label="Trial run done?"
            valuePropName="checked"
            rules={[
              {
                validator: (_, v) =>
                  executingWO?.priority === 'CRITICAL' && !v
                    ? Promise.reject(new Error('Trial run is mandatory for critical jobs'))
                    : Promise.resolve(),
              },
            ]}>
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item name="production_informed" label="Production informed?" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="closure_remarks" label="Completion remark">
            <Input.TextArea rows={2} placeholder="Any handover notes" />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Spare cost, labour cost and downtime are calculated by the system after closure.
          </Text>
        </Form>
      </Modal>

      {/* Spare request modal */}
      <Modal
        title={`Request spare for ${executingWO?.work_order_no ?? ''}`}
        open={spareModalOpen}
        onOk={() => spareForm.validateFields().then((v) => issueSpareMutation.mutate(v))}
        onCancel={() => setSpareModalOpen(false)}
        confirmLoading={issueSpareMutation.isPending}
      >
        <Form form={spareForm} layout="vertical">
          <Form.Item name="spare_id" label="Spare item" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Search by code or name"
              options={(spares ?? []).map((s: SparePart) => ({
                label: `${s.spare_code} — ${s.spare_name} (avail: ${s.current_stock} ${s.uom})`,
                value: s.id,
              }))}
              filterOption={(i, o) => (o?.label as string).toLowerCase().includes(i.toLowerCase())}
            />
          </Form.Item>
          <Form.Item
            name="issued_qty" label="Required quantity"
            rules={[{ required: true, type: 'number', min: 0.001 }]}>
            <Input type="number" min={0} step={0.5} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
